import assumptions from '../assumptions';
import controls from './controls';
import personas from './personas';
import risks from './risks';
import { v4 as uuidv4 } from 'uuid';

// Threat Dragon has no concept of threat persona, but TM-BOM threats require one
const defaultThreatPersona = {
    symbolic_name: uuidv4(),
    title: 'Default threat persona',
    description: 'Threat persona generated by export from Threat Dragon',
    is_person: false,
    skill_level: 'expert_engineer',
    access_level: 'anonymous',
    malicious_intent: true,
    applicability_to_org: 'high'
};
export const defaults = {likelihood: 'certain', riskLevelBand: 'high', source: 'adversary', threatPersona: defaultThreatPersona};

const convertImpact = {TBD: 'negligible', Low: 'minor', Medium: 'moderate', High: 'major', Critical: 'severe'};
const convertPriority = {TBD: 'none', Low: 'low', Medium: 'medium', High: 'high', Critical: 'critical'};
const convertControlStatus = {
    NotApplicable: 'wont_do',
    Open: 'under_review',
    Mitigated: 'active',
    Accepted: 'assumed',
    Transferred: 'approved',
    Avoided: 'retired',
    Eliminated: 'retired'
};

const convert = (model) => { 
    const vulns = {
        controls: [],
        risks: [],
        threats: [],
        threat_personas: [defaultThreatPersona]
    };

    // there may be no diagrams, or no cells within a diagram, or no threats for a cell
    model.detail.diagrams?.forEach((diagram) => {
        diagram.cells?.forEach((cell) => {
            cell.data.threats?.forEach((threat) => {
                const riskScore = parseInt(threat.score) || 0;
                vulns.risks.push({
                    symbolic_name: uuidv4(),
                    title: threat.title,
                    description: 'Risk: ' + threat.title,
                    threats: [threat.id],
                    likelihood: defaults.likelihood,
                    impact: convertImpact[threat.severity],
                    impact_description: 'The impact of the risk materializing is ' + convertImpact[threat.severity],
                    score: Math.round(riskScore > 10 ? riskScore / 4 : riskScore * 2.5),
                    level: defaults.riskLevelBand
                });
                vulns.controls.push({
                    symbolic_name: uuidv4(),
                    title: threat.title,
                    description: threat.description,
                    threats: [threat.id],
                    status: convertControlStatus[threat.status],
                    priority: convertPriority[threat.severity]
                });
                vulns.threats.push({
                    symbolic_name: threat.id,
                    title: threat.title,
                    description: threat.description,
                    threat_persona: defaultThreatPersona.symbolic_name,
                    event: 'Threat event generated by export from Threat Dragon',
                    sources: [defaults.source]
                });
            });
        });
    });

    return vulns;
};

const merge = (model) => {
    let tdThreats = [];

    model.threats?.forEach((threat) => {
        let description = threat.description;

        // threat.event is a value required by the schema
        description += '\nOn event : ' + threat.event;

        if (threat.attack_mechanisms) {
            const mechanisms = threat.attack_mechanisms;
            description += '\nUsing attack mechanism : ';
            mechanisms.forEach((mechanism) => {
                description += '\n' + ' CAPEC-' + mechanism.capec_id + ' : ' + mechanism.capec_title;
            });
        }

        if (threat.weaknesses) {
            const weaknesses = threat.weaknesses;
            description += '\nExploiting weakness : ';
            weaknesses.forEach((weakness) => {
                description += '\n' + ' CWE-' + weakness.cwe_id + ' : ' + weakness.cwe_title;
            });
        }

        // sources is a required key
        description += '\nExample attack vectors from :';
        for (const source of threat.sources) {
            description += '\n- ' + source.replaceAll('_', ' ');
        }

        // threat.threat_persona is a value required by the schema
        description += '\n' + personas.merge(model, threat.threat_persona);

        tdThreats.push({
            status: 'Open',
            severity: 'TBD',
            description: description,
            title: threat.title,
            type: 'Generic',
            mitigation: '',
            modelType: 'Generic',
            id: threat.symbolic_name,
            new: false,
            components_affected: threat.components_affected
        });
    });

    tdThreats = controls.merge(model, tdThreats);
    tdThreats = assumptions.merge(model, tdThreats);
    tdThreats = risks.merge(model, tdThreats);

    return tdThreats;
};

export default {
    convert,
    defaults,
    merge
};
